import type { DeviceManifest } from '../../../types/manifest'
import type { VisualKey } from './types'

function shortLabel(keywords: string): string {
  if (!keywords) return ''
  if (keywords.includes('_')) {
    const parts = keywords.split('_')
    return parts[parts.length - 1] || parts[0] || ''
  }
  return keywords
}

/**
 * 从设备 manifest 的 layout.keys 生成视觉键位网格。
 * `key` 为固件 LED 索引；额外 `ledIndexes`（如空格副灯）一并纳入。
 * 由调用方（web 层）传入具体设备的 manifest，SDK 层不依赖任何具体设备清单。
 */
export function buildVisualKeys(manifest: DeviceManifest): VisualKey[] {
  const rows = manifest.layout?.keys ?? []
  const rowCount = Math.max(rows.length, 1)
  const result: VisualKey[] = []

  // ledIndex 使用 manifest.layout 的 key 字段（固件 GetDefaultKeyMatrix 索引）。
  // 注：不要改成按数组顺序自增——0xdd 灯珠顺序经真机验证与 key 字段值一致，
  //     自增会让位置变成散点。
  rows.forEach((row, rowIndex) => {
    let cursor = 0
    const units: { start: number; width: number; key: number; label: string; leds: number[] }[] = []

    for (const item of row) {
      const width = typeof item.w === 'number' ? item.w : 1
      const leds = [item.key, ...(item.ledIndexes ?? [])]
      units.push({
        start: cursor,
        width,
        key: item.key,
        label: shortLabel(item.keywords || String(item.key)),
        leds,
      })
      cursor += width
    }

    const rowWidth = Math.max(cursor, 1)
    for (const unit of units) {
      const nx = (unit.start + unit.width / 2) / rowWidth
      const ny = (rowIndex + 0.5) / rowCount
      // 主灯
      result.push({
        ledIndex: unit.key,
        label: unit.label,
        row: rowIndex,
        col: unit.start,
        width: unit.width,
        nx,
        ny,
      })
      // 空格等副灯：沿键宽均分
      const extras = unit.leds.slice(1)
      extras.forEach((led, i) => {
        const t = (i + 1) / (extras.length + 1)
        result.push({
          ledIndex: led,
          label: '',
          row: rowIndex,
          col: unit.start + unit.width * t,
          width: 0.2,
          nx: (unit.start + unit.width * t) / rowWidth,
          ny,
        })
      })
    }
  })

  return result
}

export function getLayoutMetrics(keys: VisualKey[]) {
  const rowCount = keys.reduce((m, k) => Math.max(m, k.row + 1), 0)
  const colCount = keys.reduce((m, k) => Math.max(m, Math.ceil(k.col + k.width)), 0)
  return { rowCount, colCount }
}

/**
 * 主灯 → 副灯映射（如空格主灯 38 → 副灯 [82,83,84,85]）。
 * 宽键（空格）有多个灯珠，效果渲染时必须作为整体键亮灭，
 * 否则按各自位置独立计算会出现「空格被圆/频谱撕裂成几段」的观感。
 */
export function getBoundAuxLedMap(manifest: DeviceManifest): Map<number, number[]> {
  const map = new Map<number, number[]>()
  for (const row of manifest.layout?.keys ?? []) {
    for (const unit of row) {
      if (unit.ledIndexes?.length) {
        map.set(unit.key, unit.ledIndexes)
      }
    }
  }
  return map
}
